'use client';

import React from 'react';
import { FileText, Database, FileSpreadsheet, FileJson } from 'lucide-react';
import { Format } from '../lib/formats';
import colors from 'tailwindcss/colors';

export interface FileFormatIconProps {
  /**
   * The file format to display an icon for
   */
  format: Format;
  
  /**
   * The size of the icon in pixels
   * @default 24
   */
  size?: number;
  
  /**
   * Optional CSS class name to apply to the icon
   */
  className?: string;
  
  /**
   * Optional color for the icon
   * If not provided, the default format-specific color will be used
   */
  color?: string;
}

/**
 * Format-specific default colors using Tailwind color variables
 */
const formatColors: Record<Format, string> = {
  [Format.CSV]: colors.blue[500], // Standard blue for data files
  [Format.TSV]: colors.cyan[500], // A distinct cyan/teal color that's still in the blue family but clearly different
  [Format.JSON]: colors.amber[500], // Orange/amber for JavaScript association
  [Format.PARQUET]: colors.violet[500], // Rich purple for advanced data format
  [Format.XLSX]: colors.green[600], // Excel green, closer to Microsoft Excel's brand color
};

/**
 * Returns Tailwind CSS classes for format-specific styling
 * This can be used across the application for consistent format-specific styling
 */
export function getFormatColorClasses(format: Format) {
  switch (format) {
    case Format.CSV:
      return { 
        bg: 'bg-blue-50', 
        hover: 'hover:bg-blue-100', 
        border: 'hover:border-blue-200', 
        text: 'text-blue-600' 
      };
    case Format.TSV:
      return { 
        bg: 'bg-cyan-50', 
        hover: 'hover:bg-cyan-100', 
        border: 'hover:border-cyan-200', 
        text: 'text-cyan-600' 
      };
    case Format.JSON:
      return { 
        bg: 'bg-amber-50', 
        hover: 'hover:bg-amber-100', 
        border: 'hover:border-amber-200', 
        text: 'text-amber-600' 
      };
    case Format.PARQUET:
      return { 
        bg: 'bg-violet-50', 
        hover: 'hover:bg-violet-100', 
        border: 'hover:border-violet-200', 
        text: 'text-violet-600' 
      };
    case Format.XLSX:
      return { 
        bg: 'bg-green-50', 
        hover: 'hover:bg-green-100', 
        border: 'hover:border-green-200', 
        text: 'text-green-600' 
      };
    default:
      return { 
        bg: 'bg-gray-50', 
        hover: 'hover:bg-gray-100', 
        border: 'hover:border-gray-200', 
        text: 'text-gray-600' 
      };
  }
}

/**
 * A component that displays an icon for a specific file format
 * This can be used across the application to ensure consistent format icons
 */
export function FileFormatIcon({ 
  format, 
  size = 24, 
  className = '', 
  color
}: FileFormatIconProps) {
  // Use the provided color or the default format-specific color
  const iconColor = color || formatColors[format];
  
  // Common props for all icons
  const iconProps = {
    size,
    className,
    color: iconColor,
  };
  
  // Return the appropriate icon based on the format
  switch (format) {
    case Format.PARQUET:
      return <Database {...iconProps} />;
    case Format.JSON:
      return <FileJson {...iconProps} />;
    case Format.XLSX:
      return <FileSpreadsheet {...iconProps} />;
    case Format.TSV:
      return <FileText {...iconProps} />;
    case Format.CSV:
    default:
      return <FileText {...iconProps} />;
  }
}

/**
 * A component that displays a file format icon with a colored background
 */
export function FileFormatIconWithBackground({ 
  format, 
  size = 24, 
  className = '', 
  color
}: FileFormatIconProps) {
  // Use the provided color or the default format-specific color
  const iconColor = color || formatColors[format];
  
  // Calculate background size based on icon size
  const bgSize = Math.round(size * 1.5);
  
  // Format-specific background colors using Tailwind color variables
  const getBgColor = () => {
    switch (format) {
      case Format.CSV:
        return colors.blue[50];
      case Format.TSV:
        return colors.cyan[50];
      case Format.JSON:
        return colors.amber[50];
      case Format.PARQUET:
        return colors.violet[50];
      case Format.XLSX:
        return colors.green[50];
      default:
        return colors.gray[50];
    }
  };
  
  return (
    <div 
      className={`flex items-center justify-center rounded-full ${className}`}
      style={{ 
        width: bgSize, 
        height: bgSize, 
        backgroundColor: getBgColor() 
      }}
    >
      <FileFormatIcon format={format} size={size} color={iconColor} />
    </div>
  );
} 